import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogHeader,
} from "@/components/ui/dialog";
import { Eye, Info, Trash2, PenLine } from "lucide-react";
import { Link } from "react-router-dom";
import { blackUser } from "@/assets";
import { format, parseISO, parse } from "date-fns";
import { ReservationTableItems } from "@/components/dialog/common-dialog";
import * as Tooltip from "@radix-ui/react-tooltip";

export function TableHistoryListTable({ data = [] }) {
  const [isNoteOpen, setIsNoteOpen] = React.useState(false);
  const [selectedNote, setSelectedNote] = React.useState("");

  const statusColors = {
    draft: "text-gray-400",
    waiting: "text-yellow-300/70",
    confirmed: "text-cblue",
    cancelled: "text-cred",
    completed: "text-cgreen",
    on_show: "text-cyellow",
  };

  const [addOnsDialog, setAddOnsDialog] = useState(false);
  const [itemsDialog, setItemsDialog] = useState(null);
  const closeAddOns = () => {
    setAddOnsDialog(false);
    setItemsDialog(null);
  };

  const formatDateTimeSafely = (dateString, formatStr) => {
    if (!dateString) return "N/A";
    try {
      const normalized = dateString.includes("T") ? dateString : dateString.replace(" ", "T");
      return format(parseISO(normalized), formatStr);
    } catch (e) {
      return dateString;
    }
  };

  const formatDateSafely = (dateString, formatStr) => {
    if (!dateString) return "N/A";
    try {
      return format(parseISO(dateString), formatStr);
    } catch (e) {
      return dateString;
    }
  };


  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";

    const date = new Date(dateString);

    const yyyy = date.getUTCFullYear();
    const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
    const dd = String(date.getUTCDate()).padStart(2, "0");

    const time = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: "UTC",
    });

    return `${yyyy}-${mm}-${dd} ${time}`;
  };

  return (
    <Tooltip.Provider delayDuration={200}>
      <Table className="w-full">
        <TableHeader>
          <TableRow>
            <TableHead className="w-[140px]">ID</TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Booking Date</TableHead>
            <TableHead>Issue Date</TableHead>
            <TableHead>Start Time</TableHead>
            <TableHead>End Time</TableHead>
            <TableHead>Guest</TableHead>
            <TableHead>Advance</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Note</TableHead>
            <TableHead>Room/Table</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data && data.length > 0 ? (
            data.map((item, index) => (
              <TableRow key={index}>
                <TableCell>{item?.unique_id || "N/A"}</TableCell>
                <TableCell>{item?.name || item?.customer?.full_name || "N/A"}</TableCell>
                <TableCell className="capitalize">
                  {(item?.type || item?.reservation_type || "N/A").replace("_", " ")}
                </TableCell>
                <TableCell>
                  {formatDateSafely(item?.reservation_date, "yyyy-MM-dd")}
                </TableCell>
                <TableCell>
                  {formatDateSafely(item?.issue_date, "yyyy-MM-dd")}
                </TableCell>
                <TableCell>
                  {formatDateTimeSafely(item?.start_date_time, "yyyy-MM-dd hh:mm a")}
                </TableCell>
                <TableCell>
                  {formatDateTimeSafely(item?.end_date_time, "yyyy-MM-dd hh:mm a")}
                </TableCell>
                <TableCell>{item?.guest || "0"}</TableCell>
                <TableCell>{item?.advance || "0"}</TableCell>
                <TableCell
                  className={
                    statusColors[item?.status?.toLowerCase()] || "text-gray-300"
                  }
                >
                  {item?.status || "N/A"}
                </TableCell>
                <TableCell>
                  <Tooltip.Root>
                    <Tooltip.Trigger asChild>
                      <Eye
                        onClick={() => {
                          setSelectedNote(item?.note);
                          setIsNoteOpen(true);
                        }}
                        className="text-cwhite3 cursor-pointer"
                        size={18}
                      />
                    </Tooltip.Trigger>
                    <Tooltip.Portal>
                      <Tooltip.Content className="bg-black text-white text-xs rounded px-2 py-1">
                        View Note
                      </Tooltip.Content>
                    </Tooltip.Portal>
                  </Tooltip.Root>
                </TableCell>
                <TableCell>
                  {item?.room_table || (item?.tables && item?.tables.map(t => t.name).join(", ")) || "-"}
                </TableCell>

                <TableCell className="text-right justify-end flex gap-2 items-center">
                  <div className="flex items-center gap-2 h-10">
                    <Link to={`/table-history/booking-details/${item?.id}`}>
                      <Tooltip.Root>
                        <Tooltip.Trigger asChild>
                          <Eye
                            className="text-cwhite3 cursor-pointer"
                            size={18}
                          />
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                          <Tooltip.Content className="bg-black text-white text-xs rounded px-2 py-1">
                            View Booking Details
                          </Tooltip.Content>
                        </Tooltip.Portal>
                      </Tooltip.Root>
                    </Link>
                  </div>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={35} className="text-center">
                No data available
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <NoteDialog
        note={selectedNote}
        isOpen={isNoteOpen}
        onClose={() => {
          setIsNoteOpen(false);
          setSelectedNote("");
        }}
      />
      <ReservationTableItems
        items={itemsDialog}
        isOpen={addOnsDialog}
        onClose={closeAddOns}
      />
    </Tooltip.Provider>
  );
}

export default TableHistoryListTable;

const NoteDialog = ({ note, isOpen, onClose }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Booking Note</DialogTitle>
        </DialogHeader>
        <div className="">
          <p>{note || "No note available"}</p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
